import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import { toListQuery } from './filters';
import { subscriptionsReportListResponseSchema } from './schemas';
import type { SubscriptionsReportListParams, SubscriptionsReportListResponse } from './types';

/** The one column whose sort key the wire spells differently. `<DataTable>` reports the antd
 *  column's `dataIndex` as the sort field, and the Customer column must keep `dataIndex:
 *  'customerName'` to render the row's name — but the stub's `sortBy` enum says `customer`.
 *  Same arrangement as `admin-users`' `SORT_FIELD_MAP`. Every other column's `dataIndex` already
 *  matches the enum exactly. If BE lands `customerName` instead, delete this map. */
const SORT_FIELD_MAP: Record<string, string> = { customerName: 'customer' };

/** Sort + filters → the list's wire params, defaults included. The export sends the same, so the
 *  file comes out with the rows and order on screen, sorted or not (ADO 120974). */
export function subscriptionsReportRequestQuery(
  params: Omit<SubscriptionsReportListParams, 'page' | 'limit'>,
) {
  const { sortBy = 'createdDate', sortOrder = 'desc', ...filters } = params;
  return {
    sortBy: SORT_FIELD_MAP[sortBy] ?? sortBy,
    sortOrder: sortOrder.toUpperCase(),
    ...toListQuery(filters),
  };
}

/** Paginated / sorted / searchable Subscriptions report — **one row per Customer**.
 *
 *  Default sort is `createdDate DESC`, the **Customer record's** creation date (grilling Q10): this
 *  tab has no date column, so 112312's "newest entries first" has no visible anchor and the default
 *  key is deliberately non-visible. The consequence is intended: no column header loads in an active
 *  sorted state, and "a sort indicator is visible by default on every sortable column header" is read
 *  as *every sortable header carries the caret affordance*, which `<DataTable>` already provides.
 *
 *  `sortOrder` is uppercased at the wire boundary for the DTO's `SortOrder` enum, as
 *  `admin-users`/`warranty` already do. */
export async function listSubscriptionsReport(
  params: SubscriptionsReportListParams,
): Promise<SubscriptionsReportListResponse> {
  const { page, limit, ...rest } = params;
  const { data, meta } = await apiClient.getList(endpoints.reports.subscriptions, {
    params: { page, limit, ...subscriptionsReportRequestQuery(rest) },
  });
  return subscriptionsReportListResponseSchema.parse({ data, meta });
}
